import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import c from 'classnames';
import { Button, Icon, Typography } from '#components';
import styles from './Modal.module.css';

export interface ModalProps {
  children: ReactNode;
  className?: string;
  collapse?: boolean;
  footer?: ReactNode;
  label?: React.ReactNode;
  labelClassName?: string;
  isDismissable?: boolean; // Defaults to true
  showFloatingX?: boolean;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  preToggle?: boolean;
  showHeader?: boolean;
  title?: string;
  hasBodyPadding?: boolean; // Defaults to true, set false to remove body padding
  closeOnBackdropClick?: boolean; // Defaults to true, set false to prevent backdrop dismiss
}

export function Modal({
  children,
  className = '',
  collapse,
  footer,
  isDismissable = true,
  showFloatingX = false,
  isOpen: controlledIsOpen,
  label,
  labelClassName,
  onOpen,
  onClose,
  preToggle,
  showHeader,
  title,
  hasBodyPadding = true,
  closeOnBackdropClick = true,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (preToggle) {
      setIsOpen(true);
      onOpen?.();
    }
  }, [onOpen, preToggle]);

  useEffect(() => {
    if (controlledIsOpen !== undefined) {
      setIsOpen(controlledIsOpen);
    }
  }, [controlledIsOpen]);

  const toggle = () => {
    if (onOpen && !isOpen) {
      onOpen();
    }
    if (onClose && isOpen) {
      onClose();
    }
    setIsOpen(!isOpen);
  };
  useEffect(() => {
    setIsMounted(true); // render modal client side only
  }, []);

  // Handle Escape key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Focus trapping and restoration
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    onOpen?.();
    previousFocus.current = document.activeElement as HTMLElement;

    const modal = modalRef.current;
    modal?.focus();

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !modal) {
        return;
      }

      const focusable = Array.from(
        modal.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(el => el.offsetParent !== null);

      if (focusable.length === 0) {
        event.preventDefault();
        modal.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || active === modal || !modal.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => {
      document.removeEventListener('keydown', handleTab);
      previousFocus.current?.focus(); // Restore focus
    };
  }, [isOpen, onOpen]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = ''; // Ensure scroll is restored on unmount
    };
  }, [isOpen]);

  // Framer Motion variants for backdrop
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  // Framer Motion variants for dialog
  const dialogVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 }, // Ensure exit matches hidden for smooth transition
  };

  return (
    <>
      {!!label && (
        <div role='button' className={labelClassName} onClick={toggle}>
          {label}
        </div>
      )}
      {isMounted &&
        createPortal(
          <AnimatePresence>
            {isOpen && (
              <motion.div
                className={styles.modalBackdrop}
                data-testid='modal-backdrop'
                onClick={closeOnBackdropClick ? onClose : undefined}
                role='presentation'
                initial='hidden'
                animate='visible'
                exit='hidden'
                variants={backdropVariants}
                transition={{ duration: 0.15 }}
              >
                <motion.div
                  ref={modalRef}
                  className={`${styles.modalDialog} ${className}`}
                  role='dialog'
                  aria-modal='true'
                  aria-labelledby='modal-title'
                  tabIndex={-1}
                  onClick={e => e.stopPropagation()}
                  initial='hidden'
                  animate='visible'
                  exit='exit'
                  variants={dialogVariants}
                  transition={{ duration: 0.15 }}
                >
                  {showHeader ? (
                    <div className={styles.modalHeader}>
                      {title ? (
                        <Typography
                          id='modal-title'
                          variant='heading-l'
                          className={styles.modalTitle}
                        >
                          {title}
                        </Typography>
                      ) : (
                        <div></div>
                      )}
                      {isDismissable ? (
                        <Button
                          type='button'
                          variant='wrapper'
                          onClick={onClose}
                          aria-label='Close'
                        >
                          <Icon
                            name={collapse ? 'collapse' : 'closeSm'}
                            size='m'
                          />
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                  <div
                    className={c(
                      styles.modalBody,
                      !hasBodyPadding && styles.modalBodyNoPadding,
                    )}
                  >
                    {showFloatingX && (
                      <Button
                        type='button'
                        variant='wrapper'
                        onClick={onClose}
                        aria-label='Close'
                        className={styles.floatingCloseButton}
                      >
                        <Icon name='closeSm' size='m' />
                      </Button>
                    )}
                    {children}
                  </div>
                  {footer && <div className={styles.modalFooter}>{footer}</div>}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
}
